/**
 * Add Prompt service for submitting prompts to library
 */

import * as promptLibraryService from '../../../promptLibrary/data/services/promptLibraryService';
import * as promptBuilderController from '../../../../shared/promptForm/promptBuilderController';
import * as userService from '../../../../shared/domain/services/userService';
import { PromptSubmission } from '../../domain/models/PromptSubmission';

/**
 * Submit prompt to library
 * @param {Object} submissionData - Prompt submission data
 * @param {string} userId - User ID
 * @param {string} organizationId - Organization ID
 * @param {Object} user - Optional user object (if available from context)
 * @param {boolean} isGlobal - Whether to create a global prompt (admin only)
 * @returns {Promise<Object>} Created prompt
 */
export const submitPrompt = async (submissionData, userId, organizationId, user = null, isGlobal = false) => {
  const submission = PromptSubmission.fromData(submissionData);
  
  // Determine prompt format (default to Free Text as the standard format)
  const promptFormat = submissionData.promptFormat || 'FREE_TEXT';

  // Build prompt text based on format
  let promptText = '';
  if (promptFormat === 'FREE_TEXT') {
    promptText = submissionData.freeText || '';
  } else {
    // STRUCTURED format (legacy format, still supported for backward compatibility)
    promptText = promptBuilderController.buildPrompt(submission);
  }

  // Ensure promptText is not empty - this should be caught by frontend validation, but double-check here
  if (!promptText || promptText.trim() === '') {
    const formatName = promptFormat === 'FREE_TEXT' ? 'Free Text' : 'Structured';
    throw new Error(`Prompt text cannot be empty. Please fill in the required fields for the ${formatName} format.`);
  }

  // Get user details - use provided user or fetch from API
  let userData = user;
  if (!userData) {
    userData = await userService.getUserById(userId);
  }

  // Prepare prompt data for library
  // Backend will map: title -> title, description -> promptDescription, backgroundTheory -> promptTheory
  // Backend will resolve category names to categoryIds if needed
  const promptData = {
    title: submission.title,
    description: submission.description || '',
    promptText: promptText,
    categories: Array.isArray(submission.category) ? submission.category : (submission.category ? [submission.category] : []), // Send as array
    creatorId: userId,
    creatorName: userData.name || 'Unknown',
    creatorEmail: userData.email || '',
    organizationId: organizationId,
    orgId: organizationId, // Also send as orgId for backend compatibility
    backgroundTheory: submission.backgroundTheory || null,
    unpaidCopilot: submission.unpaidCopilot === 'yes' || submission.unpaidCopilot === true,
    promptFormat: promptFormat,
    isGlobal: isGlobal, // Pass isGlobal flag for backend
  };
  
  // Debug logging
  console.log('[addPromptService] Submitting prompt data:', {
    organizationId: promptData.organizationId,
    orgId: promptData.orgId,
    isGlobal: promptData.isGlobal,
    userId: promptData.creatorId
  });

  // Use global prompt endpoint if isGlobal is true, otherwise use regular endpoint
  // Backend will handle orgId assignment based on isGlobal flag and admin status
  const prompt = isGlobal 
    ? await promptLibraryService.addGlobalPromptToLibrary(promptData)
    : await promptLibraryService.addPromptToLibrary(promptData);

  return prompt;
};

